/** Super Admin → Partner Billing: set setup fee + monthly subscription, charge Schedule C equipment, see and retry payments. */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, PageHeader, StatusPill, btnGhost, btnPrimary, btnSmall, errorText, inputCls, money } from '../../components/finance/financeUi';
import { ChargesTable } from '../../components/billing/ChargesTable';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { WHO_PAYS, WhoPaysFields } from '../../components/billing/WhoPaysFields';

function PlanForm({ d, onDone }) {
  const [f, setF] = useState({ setup_fee: d.plan?.setup_fee ?? '', monthly_fee: d.plan?.monthly_fee ?? '', billing_responsibility: d.plan?.billing_responsibility || 'USER_PAYS' });
  const [status, setStatus] = useState(d.plan?.status || 'active');
  const save = async () => {
    try { await api.request(`/admin/billing/partners/${d.user_id}/plan`, { method: 'PUT', body: JSON.stringify({ ...f, status }) }); toast.success('Billing plan saved'); onDone(); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="space-y-2" data-testid="plan-form">
      <WhoPaysFields value={f} onChange={setF} testid="plan" />
      <div className="flex flex-wrap items-end gap-2">
        <label className="w-40 text-xs">Status
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-10" data-testid="plan-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem></SelectContent>
          </Select>
        </label>
        <button type="button" className={btnPrimary} onClick={save} data-testid="plan-save-btn">Save plan</button>
      </div>
    </div>
  );
}

function EventsDialog({ charge, onClose }) {
  const [ev, setEv] = useState(null);
  useEffect(() => { api.request(`/admin/billing/charges/${charge.charge_id}/events`).then((r) => setEv(r.events)).catch((e) => toast.error(errorText(e))); }, [charge]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="charge-events-dialog">
        <DialogHeader><DialogTitle>{charge.description}</DialogTitle></DialogHeader>
        {!ev ? <p className="text-sm text-muted-foreground">Loading…</p> : !ev.length ? <p className="text-sm text-muted-foreground">No payment events yet.</p> : (
          <ol className="space-y-2 text-sm">{ev.map((e) => (
            <li key={e.event_id} className="flex justify-between gap-3 border-b pb-2" data-testid={`charge-event-${e.event_id}`}>
              <span><StatusPill value={e.to_status} /> <span className="ml-1 text-xs text-muted-foreground">via {e.source} · {e.provider}</span>{e.reason && <span className="block text-xs text-destructive">{e.reason}</span>}</span>
              <span className="text-xs text-muted-foreground">{new Date(e.at).toLocaleString()}</span>
            </li>))}</ol>)}
      </DialogContent>
    </Dialog>
  );
}

function EquipmentForm({ d, onDone }) {
  const [qty, setQty] = useState({});
  if (!d.schedule_c.length) return <p className="text-sm text-muted-foreground" data-testid="equipment-none">No Schedule C items are published for this partner type yet.</p>;
  const items = Object.entries(qty).filter(([, q]) => Number(q) > 0).map(([name, q]) => ({ name, qty: Number(q) }));
  const total = items.reduce((s, i) => s + i.qty * (d.schedule_c.find((c) => c.name === i.name)?.cost || 0), 0);
  const charge = async () => {
    try { await api.request(`/admin/billing/partners/${d.user_id}/equipment`, { method: 'POST', body: JSON.stringify({ items }) }); toast.success('Equipment charge created'); setQty({}); onDone(); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="space-y-2" data-testid="equipment-form">
      {d.schedule_c.map((c, i) => (
        <div key={c.name} className="flex items-center gap-3 text-sm">
          <span className="flex-1">{c.name}<span className="ml-2 text-xs text-muted-foreground">{c.includes}</span></span>
          <span className="tabular-nums">{money(c.cost)}</span>
          <input className="h-10 w-20 shrink-0 rounded-md border bg-background px-3 text-sm" type="number" min="0" placeholder="Qty" value={qty[c.name] || ''} onChange={(e) => setQty({ ...qty, [c.name]: e.target.value })} data-testid={`equipment-qty-${i}`} />
        </div>
      ))}
      <button type="button" className={btnPrimary} disabled={!items.length} onClick={charge} data-testid="equipment-charge-btn">Charge {money(total)}</button>
    </div>
  );
}

function PartnerDialog({ userId, name, onClose }) {
  const [d, setD] = useState(null);
  const [events, setEvents] = useState(null);
  const load = useCallback(() => api.request(`/admin/billing/partners/${userId}`).then(setD).catch((e) => toast.error(errorText(e))), [userId]);
  useEffect(() => { load(); }, [load]);
  const act = (c, verb) => {
    if (verb === 'void' && !window.confirm(`Void this ${money(c.amount)} charge? The partner will no longer owe it.`)) return;
    if (verb === 'refund' && !window.confirm(`Refund ${money(c.amount)} to the partner's card?`)) return;
    api.request(`/admin/billing/charges/${c.charge_id}/${verb}`, { method: 'POST' }).then((r) => {
      toast[r.status === 'failed' ? 'error' : 'success'](verb === 'void' ? 'Charge voided' : verb === 'refund' ? 'Refund issued' : r.status === 'paid' ? 'Charge collected' : r.status === 'failed' ? `Retry failed: ${r.last_error}` : 'Retry sent');
      load();
    }).catch((e) => toast.error(errorText(e)));
  };
  const actions = (c) => (
    <span className="flex justify-end gap-1">
      {['due', 'failed'].includes(c.status) && <><button type="button" className={btnSmall} onClick={() => act(c, 'retry')} data-testid={`charge-retry-${c.charge_id}`}>Retry</button>
        <button type="button" className={btnSmall} onClick={() => act(c, 'void')} data-testid={`charge-void-${c.charge_id}`}>Void</button></>}
      {c.status === 'paid' && c.method_type === 'card' && <button type="button" className={btnSmall} onClick={() => act(c, 'refund')} data-testid={`charge-refund-${c.charge_id}`}>Refund</button>}
      <button type="button" className={btnSmall} onClick={() => setEvents(c)} data-testid={`charge-history-${c.charge_id}`}>History</button>
    </span>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="partner-billing-dialog">
        <DialogHeader><DialogTitle>{name}</DialogTitle></DialogHeader>
        {!d ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="min-w-0 space-y-5">
            <p className="text-sm" data-testid="partner-billing-method">{d.method ? `On file: ${d.method.brand} ending ${d.method.last4}` : 'No payment method on file yet — charges wait until the partner adds one.'}</p>
            <section className="space-y-2"><h3 className="text-sm font-medium">Who pays, setup fee & subscription</h3><PlanForm key={JSON.stringify(d.plan)} d={d} onDone={load} /></section>
            {(d.plan?.billing_responsibility || 'USER_PAYS') === 'USER_PAYS' && <section className="space-y-2"><h3 className="text-sm font-medium">Charge Schedule C equipment</h3><EquipmentForm d={d} onDone={load} /></section>}
            <section className="rounded-lg border"><ChargesTable charges={d.charges} testid="partner-charges" actions={actions} /></section>
            {events && <EventsDialog charge={events} onClose={() => setEvents(null)} />}
          </div>)}
      </DialogContent>
    </Dialog>
  );
}

export default function PartnerBillingPage() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState('');
  const load = useCallback(() => api.request('/admin/billing/partners').then(setD).catch((e) => toast.error(errorText(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  const rows = d.rows.filter((r) => `${r.name} ${r.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-6" data-testid="partner-billing-page">
      <PageHeader eyebrow="Payments in" title="Partner Billing" subtitle="Setup fees, Schedule C equipment and monthly software subscriptions, collected by card or bank debit through Maverick." />
      {d.mode === 'simulated' && <p className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" data-testid="partner-billing-test-mode">Test mode — payments are simulated until Maverick card and ACH access is switched on.</p>}
      <div className="grid gap-4 sm:grid-cols-3">
        {[['Balance due', money(d.totals.due), 'due'], ['Collected', money(d.totals.collected), 'collected'], ['Failed charges', d.totals.failed, 'failed']].map(([l, v, k]) => (
          <Card key={k} testid={`billing-total-${k}`}><div className="px-5 py-4"><p className="text-xs uppercase tracking-wide text-muted-foreground">{l}</p><p className="mt-1 text-3xl font-semibold tabular-nums">{v}</p></div></Card>))}
      </div>
      <Card title={`${rows.length} partners`} aside={<input className={`${inputCls} max-w-[14rem]`} placeholder="Search partners" value={q} onChange={(e) => setQ(e.target.value)} data-testid="partner-billing-search" />}>
        <div className="divide-y">{rows.map((r) => (
          <div key={r.user_id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm" data-testid={`billing-partner-${r.user_id}`}>
            <div className="min-w-0 flex-1"><p className="font-medium">{r.name}</p><p className="truncate text-xs text-muted-foreground">{r.email} · {r.partner_type}</p></div>
            <span className="text-xs text-muted-foreground">{r.billing_responsibility && r.billing_responsibility !== 'USER_PAYS' ? WHO_PAYS[r.billing_responsibility] : r.monthly_fee ? `${money(r.monthly_fee)}/mo` : 'No plan'}</span>
            <span className="text-xs">{r.method || 'No method'}</span>
            {r.plan_status && <StatusPill value={r.plan_status} />}
            <span className={`w-24 text-right tabular-nums ${r.balance_due ? 'font-semibold' : 'text-muted-foreground'}`}>{money(r.balance_due)}</span>
            <button type="button" className={btnGhost} onClick={() => setOpen(r)} data-testid={`billing-manage-${r.user_id}`}>Manage</button>
          </div>))}
        </div>
      </Card>
      {open && <PartnerDialog userId={open.user_id} name={open.name} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
}
